import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

interface UserProfile {
  id: string;
  el_pastas: string;
  vardas: string;
  telefonas: string;
  profesijos_tipas: string;
  darbo_regionas: string;
  statusas: string;
  sukurta_data: string;
  atnaujinta_data: string;
  profession_data: any;
  ai_prompts: any[];
}

const AdminUserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [usageData, setUsageData] = useState<any>(null);
  const [paymentsData, setPaymentsData] = useState<any>(null);
  const [aiPromptsData, setAiPromptsData] = useState<any>(null);
  const [aiPromptsLoading, setAiPromptsLoading] = useState(false);
  const [activityData, setActivityData] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState<string>('');
  const [isEditingSummaryPrompt, setIsEditingSummaryPrompt] = useState(false);
  const [editedSummarySystemPrompt, setEditedSummarySystemPrompt] = useState<string>('');
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedUser, setEditedUser] = useState<UserProfile | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userId) {
      setError('Vartotojo ID nerastas');
      setLoading(false);
      return;
    }

    fetchUserProfile();
  }, [userId]);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Handle different response structures
        const userData = data.data?.user || data.user || data;

        // Map backend field names to frontend expected names
        if (userData.professionData) {
          userData.profession_data = userData.professionData;
          delete userData.professionData;
        }
        if (userData.aiPromptData) {
          userData.ai_prompts = [userData.aiPromptData];
          delete userData.aiPromptData;
        }

        setUser(userData);
        setEditedUser(userData);

        // Fetch additional data for all tabs immediately
        fetchUsageData();
        fetchPaymentsData();
        fetchAiPromptsData();
        fetchActivityData();
      } else {
        setError('Vartotojas nerastas');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setError('Klaida kraunant vartotojo duomenis');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageData = async () => {
    if (!userId) return;

    try {
      const response = await fetch(`/api/admin/usage/${userId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUsageData(data.data);
      }
    } catch (error) {
      console.error('Error fetching usage data:', error);
    }
  };

  const fetchPaymentsData = async () => {
    if (!userId) return;

    try {
      const response = await fetch(`/api/admin/payments/${userId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentsData(data.data);
      }
    } catch (error) {
      console.error('Error fetching payments data:', error);
    }
  };

  const fetchAiPromptsData = async () => {
    if (!userId) return;

    try {
      setAiPromptsLoading(true);
      const response = await fetch(`/api/admin/ai-prompts/${userId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAiPromptsData(data.data);
      } else {
        console.error('Failed to fetch AI prompts data');
      }
    } catch (error) {
      console.error('Error fetching AI prompts data:', error);
    } finally {
      setAiPromptsLoading(false);
    }
  };

  const fetchActivityData = async () => {
    if (!userId) return;

    try {
      setActivityLoading(true);
      const response = await fetch(`/api/admin/user/${userId}/activity?limit=50`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setActivityData(data.activities);
        }
      } else {
        console.error('Failed to fetch activity data');
      }
    } catch (error) {
      console.error('Error fetching activity data:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!user) return;

    if (window.confirm(`Ar tikrai norite ištrinti vartotoją ${user.vardas}? Šis veiksmas negrįžtamas.`)) {
      try {
        const response = await fetch(`/api/admin/users/${user.id}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (response.ok) {
          alert('Vartotojas sėkmingai ištrintas');
          navigate('/admin');
        } else {
          const data = await response.json();
          alert('Klaida trinant vartotoją: ' + data.message);
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Klaida trinant vartotoją');
      }
    }
  };

  const handleSaveChanges = async () => {
    if (!editedUser) return;

    setSaving(true);
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(editedUser)
      });

      if (response.ok) {
        setUser(editedUser);
        setIsEditing(false);
        alert('Vartotojo duomenys sėkmingai atnaujinti');
      } else {
        const data = await response.json();
        alert('Klaida atnaujinant duomenis: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Klaida atnaujinant vartotojo duomenis');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedUser(user);
    setIsEditing(false);
  };

  const handleFieldChange = (field: string, value: any) => {
    if (!editedUser) return;
    setEditedUser({
      ...editedUser,
      [field]: value
    });
  };

  const handleProfessionDataChange = (field: string, value: any) => {
    if (!editedUser) return;
    setEditedUser({
      ...editedUser,
      profession_data: {
        ...editedUser.profession_data,
        [field]: value
      }
    });
  };

  const startFieldEdit = (field: string) => {
    setEditingField(field);
    if (!editedUser) {
      setEditedUser(user);
    }
  };

  const saveFieldEdit = async (field: string) => {
    if (!editedUser || !user) return;

    setSaving(true);
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(editedUser)
      });

      if (response.ok) {
        setUser(editedUser);
        setEditingField(null);
      } else {
        const data = await response.json();
        alert('Klaida atnaujinant duomenis: ' + data.message);
        setEditedUser(user); // Reset to original
      }
    } catch (error) {
      console.error('Error updating field:', error);
      alert('Klaida atnaujinant duomenis');
      setEditedUser(user); // Reset to original
    } finally {
      setSaving(false);
    }
  };

  const cancelFieldEdit = () => {
    setEditingField(null);
    setEditedUser(user);
  };

  const startPromptEdit = () => {
    // Get the stored prompt (the one actually saved in DB and used by AI)
    const storedPromptData = aiPromptsData?.stored_prompt?.prompto_duomenys;
    const currentPrompt = storedPromptData?.generated_prompt || storedPromptData?.prompt || '';

    const promptText = typeof currentPrompt === 'object'
      ? currentPrompt.prompt || JSON.stringify(currentPrompt, null, 2)
      : currentPrompt || '';
    setEditedPrompt(promptText);
    setIsEditingPrompt(true);
  };

  const savePromptEdit = async () => {
    if (!userId || !editedPrompt) return;

    setSaving(true);
    try {
      const response = await fetch('/api/admin/prompt/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          userId,
          profession: user?.profesijos_tipas,
          prompt: editedPrompt
        })
      });

      if (response.ok) {
        alert('AI prompt sėkmingai atnaujintas');
        setIsEditingPrompt(false);
        // Refresh the AI prompts data
        await fetchAiPromptsData();
      } else {
        const data = await response.json();
        alert('Klaida atnaujinant prompt: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
      alert('Klaida atnaujinant AI prompt');
    } finally {
      setSaving(false);
    }
  };

  const cancelPromptEdit = () => {
    setIsEditingPrompt(false);
    setEditedPrompt('');
  };

  const startSummaryPromptEdit = () => {
    const systemPrompt = aiPromptsData?.summary_prompt?.system_prompt || '';
    setEditedSummarySystemPrompt(systemPrompt);
    setIsEditingSummaryPrompt(true);
  };

  const saveSummaryPromptEdit = async () => {
    if (!userId || !editedSummarySystemPrompt) return;

    setSaving(true);
    try {
      const response = await fetch(`/api/admin/ai-prompts/${userId}/summary`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          system_prompt: editedSummarySystemPrompt
        })
      });

      if (response.ok) {
        alert('✅ Santraukos promptas sėkmingai išsaugotas!');
        setIsEditingSummaryPrompt(false);
        fetchAiPromptsData(); // Reload to show updated prompt
      } else {
        const error = await response.json();
        alert(`❌ Klaida: ${error.details || 'Nepavyko išsaugoti'}`);
      }
    } catch (error) {
      console.error('Error saving summary prompt:', error);
      alert('❌ Klaida išsaugant santraukos promptą');
    } finally {
      setSaving(false);
    }
  };

  const cancelSummaryPromptEdit = () => {
    setIsEditingSummaryPrompt(false);
    setEditedSummarySystemPrompt('');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8f9fa',
        padding: '2rem',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          padding: '3rem',
          textAlign: 'center',
          fontSize: '1.2rem',
          color: '#333'
        }}>
          Kraunama...
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8f9fa',
        padding: '2rem',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          padding: '3rem',
          textAlign: 'center',
          color: '#333'
        }}>
          <h2 style={{ color: '#d32f2f', marginBottom: '1rem' }}>Klaida</h2>
          <p style={{ marginBottom: '2rem' }}>{error}</p>
          <button
            style={{
              background: '#000042',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
            onClick={() => navigate('/admin')}
          >
            Grįžti į administravimą
          </button>
        </div>
      </div>
    );
  }

  const getProfessionLabel = (profession: string) => {
    const labels: Record<string, string> = {
      dazytojas: ' Dažytojas',
      stalius: ' Stalius',
      santechnikas: '🚰 Santechnikas',
      elektrikas: ' Elektrikas',
      stogdengys: ' Stogdengys'
    };
    return labels[profession] || profession;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '2rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div className="dashboard-container admin-dashboard" style={{
        background: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        padding: '2rem',
        maxWidth: '1200px',
        margin: '0 auto',
        color: '#333'
      }}>
        <div className="dashboard-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          paddingBottom: '1rem',
          borderBottom: '2px solid #e0e0e0'
        }}>
          <h1 style={{ color: '#000042', margin: 0, fontSize: '2rem' }}> Vartotojo profilis</h1>
          <div className="user-actions" style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
              onClick={() => navigate('/admin')}
            >
              ← Grįžti
            </button>
            {!isEditing ? (
              <>
                <button
                  style={{
                    background: '#000042',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                  onClick={() => setIsEditing(true)}
                >
                   Redaguoti
                </button>
                <button
                  style={{
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                  onClick={handleDeleteUser}
                >
                   Ištrinti vartotoją
                </button>
              </>
            ) : (
              <>
                <button
                  style={{
                    background: saving ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem'
                  }}
                  onClick={handleSaveChanges}
                  disabled={saving}
                >
                  {saving ? 'Išsaugoma...' : ' Išsaugoti'}
                </button>
                <button
                  style={{
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                  onClick={handleCancelEdit}
                >
                   Atšaukti
                </button>
              </>
            )}
          </div>
        </div>

        <div className="profile-header" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #000042 0%, #1a1a5c 100%)',
          borderRadius: '8px',
          marginBottom: '2rem',
          color: 'white'
        }}>
          <div className="profile-avatar" style={{
            fontSize: '3rem',
            width: '80px',
            height: '80px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {getProfessionLabel(user.profesijos_tipas).split(' ')[0]}
          </div>
          <div className="profile-info">
            <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.8rem', color: 'white' }}>{user.vardas}</h2>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', opacity: 0.9 }}>{getProfessionLabel(user.profesijos_tipas)}</p>
            <span style={{
              background: user.statusas.toLowerCase() === 'aktyvus' ? '#28a745' : '#ffc107',
              color: user.statusas.toLowerCase() === 'aktyvus' ? 'white' : '#212529',
              padding: '0.25rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}>
              {user.statusas}
            </span>
          </div>
        </div>

        <div className="dashboard-tabs" style={{
          display: 'flex',
          gap: '0.25rem',
          marginBottom: '2rem',
          borderBottom: '2px solid #e0e0e0',
          overflowX: 'auto'
        }}>
          <button
            style={{
              background: activeTab === 'basic' ? '#000042' : 'transparent',
              color: activeTab === 'basic' ? 'white' : '#333',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              borderBottom: activeTab === 'basic' ? '2px solid #000042' : '2px solid transparent'
            }}
            onClick={() => setActiveTab('basic')}
          >
             Pagrindiniai duomenys
          </button>
          <button
            style={{
              background: activeTab === 'profession' ? '#000042' : 'transparent',
              color: activeTab === 'profession' ? 'white' : '#333',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              borderBottom: activeTab === 'profession' ? '2px solid #000042' : '2px solid transparent'
            }}
            onClick={() => setActiveTab('profession')}
          >
             Profesijos duomenys
          </button>
          <button
            style={{
              background: activeTab === 'ai' ? '#000042' : 'transparent',
              color: activeTab === 'ai' ? 'white' : '#333',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              borderBottom: activeTab === 'ai' ? '2px solid #000042' : '2px solid transparent'
            }}
            onClick={() => {
              setActiveTab('ai');
              if (!aiPromptsData) fetchAiPromptsData();
            }}
          >
             AI Promptas
          </button>
          <button
            style={{
              background: activeTab === 'usage' ? '#000042' : 'transparent',
              color: activeTab === 'usage' ? 'white' : '#333',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              borderBottom: activeTab === 'usage' ? '2px solid #000042' : '2px solid transparent'
            }}
            onClick={() => {
              setActiveTab('usage');
              if (!usageData) fetchUsageData();
            }}
          >
             Naudojimo statistika
          </button>
          <button
            style={{
              background: activeTab === 'payments' ? '#000042' : 'transparent',
              color: activeTab === 'payments' ? 'white' : '#333',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              borderBottom: activeTab === 'payments' ? '2px solid #000042' : '2px solid transparent'
            }}
            onClick={() => {
              setActiveTab('payments');
              if (!paymentsData) fetchPaymentsData();
            }}
          >
             Mokėjimai
          </button>
          <button
            style={{
              background: activeTab === 'activity' ? '#000042' : 'transparent',
              color: activeTab === 'activity' ? 'white' : '#333',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              borderBottom: activeTab === 'activity' ? '2px solid #000042' : '2px solid transparent'
            }}
            onClick={() => setActiveTab('activity')}
          >
             Veiklos istorija
          </button>
        </div>

        <div className="dashboard-content">
          {activeTab === 'basic' && (
            <div className="profile-section" style={{
              background: '#f8f9fa',
              padding: '2rem',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <h3 style={{ color: '#000042', marginBottom: '1.5rem', fontSize: '1.5rem' }}> Pagrindiniai duomenys</h3>
              <div className="info-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '1.5rem'
              }}>
                <div className="info-item" style={{
                  background: 'white',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0'
                }}>
                  <label style={{
                    display: 'block',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem'
                  }}>Vardas:</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {editingField === 'vardas' ? (
                      <>
                        <input
                          type="text"
                          value={editedUser?.vardas || ''}
                          onChange={(e) => handleFieldChange('vardas', e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        />
                        <button
                          onClick={() => saveFieldEdit('vardas')}
                          disabled={saving}
                          style={{ background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={cancelFieldEdit}
                          style={{ background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✗
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{ flex: 1 }}>{user.vardas}</span>
                        <button
                          onClick={() => startFieldEdit('vardas')}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                          title="Redaguoti vardą"
                        >
                          
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <label>El. pašto adresas:</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {editingField === 'el_pastas' ? (
                      <>
                        <input
                          type="email"
                          value={editedUser?.el_pastas || ''}
                          onChange={(e) => handleFieldChange('el_pastas', e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        />
                        <button
                          onClick={() => saveFieldEdit('el_pastas')}
                          disabled={saving}
                          style={{ background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={cancelFieldEdit}
                          style={{ background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✗
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{ flex: 1 }}>{user.el_pastas}</span>
                        <button
                          onClick={() => startFieldEdit('el_pastas')}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                          title="Redaguoti el. paštą"
                        >
                          
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <label>Telefonas:</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {editingField === 'telefonas' ? (
                      <>
                        <input
                          type="tel"
                          value={editedUser?.telefonas || ''}
                          onChange={(e) => handleFieldChange('telefonas', e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        />
                        <button
                          onClick={() => saveFieldEdit('telefonas')}
                          disabled={saving}
                          style={{ background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={cancelFieldEdit}
                          style={{ background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✗
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{ flex: 1 }}>{user.telefonas}</span>
                        <button
                          onClick={() => startFieldEdit('telefonas')}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                          title="Redaguoti telefoną"
                        >
                          
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <label>Profesija:</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {editingField === 'profesijos_tipas' ? (
                      <>
                        <select
                          value={editedUser?.profesijos_tipas || ''}
                          onChange={(e) => handleFieldChange('profesijos_tipas', e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        >
                          <option value="dazytojas"> Dažytojas</option>
                          <option value="stalius"> Stalius</option>
                          <option value="santechnikas">🚰 Santechnikas</option>
                          <option value="elektrikas"> Elektrikas</option>
                          <option value="stogdengys"> Stogdengys</option>
                        </select>
                        <button
                          onClick={() => saveFieldEdit('profesijos_tipas')}
                          disabled={saving}
                          style={{ background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={cancelFieldEdit}
                          style={{ background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✗
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{ flex: 1 }}>{getProfessionLabel(user.profesijos_tipas)}</span>
                        <button
                          onClick={() => startFieldEdit('profesijos_tipas')}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                          title="Redaguoti profesiją"
                        >
                          
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <label>Darbo regionas:</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {editingField === 'darbo_regionas' ? (
                      <>
                        <input
                          type="text"
                          value={editedUser?.darbo_regionas || ''}
                          onChange={(e) => handleFieldChange('darbo_regionas', e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        />
                        <button
                          onClick={() => saveFieldEdit('darbo_regionas')}
                          disabled={saving}
                          style={{ background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={cancelFieldEdit}
                          style={{ background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✗
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{ flex: 1 }}>{user.darbo_regionas}</span>
                        <button
                          onClick={() => startFieldEdit('darbo_regionas')}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                          title="Redaguoti darbo regioną"
                        >
                          
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <label>Statusas:</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {editingField === 'statusas' ? (
                      <>
                        <select
                          value={editedUser?.statusas || ''}
                          onChange={(e) => handleFieldChange('statusas', e.target.value)}
                          className="form-input"
                          style={{ flex: 1 }}
                        >
                          <option value="aktyvus">Aktyvus</option>
                          <option value="neaktyvus">Neaktyvus</option>
                          <option value="sustabdytas">Sustabdytas</option>
                        </select>
                        <button
                          onClick={() => saveFieldEdit('statusas')}
                          disabled={saving}
                          style={{ background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={cancelFieldEdit}
                          style={{ background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}
                        >
                          ✗
                        </button>
                      </>
                    ) : (
                      <>
                        <span className={`status-badge ${user.statusas.toLowerCase()}`} style={{ flex: 1 }}>
                          {user.statusas}
                        </span>
                        <button
                          onClick={() => startFieldEdit('statusas')}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                          title="Redaguoti statusą"
                        >
                          
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <label>Registracijos data:</label>
                  <span>{formatDate(user.sukurta_data)}</span>
                </div>
                <div className="info-item">
                  <label>Paskutinis atnaujinimas:</label>
                  <span>{formatDate(user.atnaujinta_data)}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'profession' && (
            <div className="profile-section">
              <h3> Profesijos specifiniai duomenys</h3>
              {user.profession_data ? (
                <div className="profession-data" style={{
                  background: '#f8f9fa',
                  padding: '2rem',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <div className="info-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '1.5rem'
                  }}>
                    {Object.entries(user.profession_data).map(([key, value]) => {
                      if (key === 'id' || key === 'vartotojo_id' || key === 'sukurta_data') return null;

                      let displayValue = value;
                      if (Array.isArray(value)) {
                        if (value.length === 0) {
                          displayValue = 'Nenurodyta';
                        } else if (typeof value[0] === 'object' && value[0] !== null) {
                          // Array of objects (like paslaugos_su_kainomis)
                          displayValue = value.map(item => {
                            if (item.pavadinimas && item.kaina) {
                              return `${item.pavadinimas}: €${item.kaina}/${item.vienetas || 'vnt'}`;
                            } else if (item.paslauga && item.kaina) {
                              return `${item.paslauga}: €${item.kaina}/${item.vienetas || 'vnt'}`;
                            }
                            return JSON.stringify(item);
                          }).join(', ');
                        } else {
                          // Array of strings
                          displayValue = value.join(', ');
                        }
                      } else if (typeof value === 'boolean') {
                        displayValue = value ? 'Taip' : 'Ne';
                      } else if (value === null || value === undefined || value === '') {
                        displayValue = 'Nenurodyta';
                      }

                      const getLabelForKey = (key: string) => {
                        const labels: Record<string, string> = {
                          'kaina_uz_kv_m': 'Kaina už kv.m',
                          'darbo_valandos': 'Darbo valandos',
                          'paslaugos': 'Teikiamos paslaugos',
                          'darbo_metai': 'Darbo patirtis (metais)',
                          'sertifikatai': 'Sertifikatai',
                          'papildoma_info': 'Papildoma informacija',
                          'vartotojo_id': 'Vartotojo ID',
                          'sukurta_data': 'Sukūrimo data'
                        };
                        return labels[key] || key.replace(/_/g, ' ');
                      };

                      return (
                        <div key={key} className="info-item" style={{
                          background: 'white',
                          padding: '1rem',
                          borderRadius: '6px',
                          border: '1px solid #e0e0e0'
                        }}>
                          <label style={{
                            display: 'block',
                            fontWeight: '600',
                            color: '#333',
                            marginBottom: '0.5rem',
                            fontSize: '0.9rem'
                          }}>{getLabelForKey(key)}:</label>
                          {isEditing ? (
                            typeof value === 'boolean' ? (
                              <select
                                value={editedUser?.profession_data?.[key] ? 'true' : 'false'}
                                onChange={(e) => handleProfessionDataChange(key, e.target.value === 'true')}
                                className="form-input"
                              >
                                <option value="true">Taip</option>
                                <option value="false">Ne</option>
                              </select>
                            ) : Array.isArray(value) ? (
                              <input
                                type="text"
                                value={Array.isArray(editedUser?.profession_data?.[key])
                                  ? editedUser.profession_data[key].join(', ')
                                  : editedUser?.profession_data?.[key] || ''}
                                onChange={(e) => handleProfessionDataChange(key, e.target.value.split(', ').filter(v => v.trim()))}
                                className="form-input"
                                placeholder="Atskirti kableliais"
                              />
                            ) : (
                              <input
                                type="text"
                                value={editedUser?.profession_data?.[key] || ''}
                                onChange={(e) => handleProfessionDataChange(key, e.target.value)}
                                className="form-input"
                              />
                            )
                          ) : (
                            <span style={{
                              color: displayValue === 'Nenurodyta' ? '#6c757d' : '#212529',
                              fontStyle: displayValue === 'Nenurodyta' ? 'italic' : 'normal'
                            }}>{String(displayValue)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p>Profesijos duomenys nerasti arba dar nepateikti.</p>
              )}
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="profile-section" style={{
              background: '#f8f9fa',
              padding: '2rem',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ color: '#000042', margin: 0, fontSize: '1.5rem' }}> AI Promptas</h3>
              </div>

              {aiPromptsLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  ⏳ Kraunami AI prompt duomenys...
                </div>
              ) : aiPromptsData ? (
                <div className="ai-prompts">
                  {aiPromptsData.message && (
                    <div style={{
                      padding: '1rem',
                      background: '#fff3cd',
                      borderRadius: '6px',
                      marginBottom: '1rem',
                      color: '#856404'
                    }}>
                       {aiPromptsData.message}
                    </div>
                  )}

                  {aiPromptsData.user_info && (
                    <div style={{
                      padding: '1rem',
                      background: 'white',
                      borderRadius: '8px',
                      marginBottom: '1.5rem',
                      border: '1px solid #e0e0e0'
                    }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#000042' }}> Vartotojo informacija</h4>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>
                        <p><strong>Vardas:</strong> {aiPromptsData.user_info.vardas}</p>
                        <p><strong>Profesija:</strong> {aiPromptsData.user_info.profesijos_tipas}</p>
                        <p><strong>Asistentės vardas:</strong> {aiPromptsData.user_info.asistentes_vardas || 'Nenurodyta'}</p>
                      </div>
                    </div>
                  )}

                  {aiPromptsData.stored_prompt && (
                    <div style={{
                      padding: '1.5rem',
                      background: 'white',
                      borderRadius: '8px',
                      marginBottom: '1.5rem',
                      border: '2px solid #28a745'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h4 style={{ margin: 0, color: '#28a745' }}> Dabartinis AI Promptas</h4>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <span style={{
                            background: '#28a745',
                            color: 'white',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.8rem'
                          }}>
                            Aktyvus
                          </span>
                          {!isEditingPrompt && (
                            <button
                              onClick={startPromptEdit}
                              style={{
                                background: '#000042',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                            >
                               Redaguoti promptą
                            </button>
                          )}
                        </div>
                      </div>

                      {isEditingPrompt ? (
                        <>
                          <textarea
                            value={editedPrompt}
                            onChange={(e) => setEditedPrompt(e.target.value)}
                            style={{
                              width: '100%',
                              minHeight: '400px',
                              padding: '1rem',
                              borderRadius: '6px',
                              border: '2px solid #000042',
                              fontFamily: 'monospace',
                              fontSize: '0.85rem',
                              lineHeight: '1.4',
                              resize: 'vertical',
                              marginBottom: '1rem'
                            }}
                          />
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                            <button
                              onClick={savePromptEdit}
                              disabled={saving}
                              style={{
                                background: saving ? '#6c757d' : '#28a745',
                                color: 'white',
                                border: 'none',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '4px',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500'
                              }}
                            >
                              {saving ? '⏳ Išsaugoma...' : '✓ Išsaugoti promptą'}
                            </button>
                            <button
                              onClick={cancelPromptEdit}
                              disabled={saving}
                              style={{
                                background: '#6c757d',
                                color: 'white',
                                border: 'none',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '4px',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                fontSize: '0.9rem'
                              }}
                            >
                              ✗ Atšaukti
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                            background: '#f8f9fa',
                            padding: '1rem',
                            borderRadius: '6px',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            lineHeight: '1.4',
                            whiteSpace: 'pre-wrap',
                            border: '1px solid #e0e0e0'
                          }}>
                            {(() => {
                              const storedPromptData = aiPromptsData.stored_prompt.prompto_duomenys;
                              const promptContent = storedPromptData?.generated_prompt || storedPromptData?.prompt || '';

                              return typeof promptContent === 'object'
                                ? promptContent.prompt || JSON.stringify(promptContent, null, 2)
                                : promptContent;
                            })()}
                          </div>

                          <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#666' }}>
                            <strong>Išsaugota:</strong> {formatDate(aiPromptsData.stored_prompt.sukurta_data)}
                            {aiPromptsData.stored_prompt.atnaujinta_data && (
                              <span style={{ marginLeft: '1rem' }}>
                                <strong>Atnaujinta:</strong> {formatDate(aiPromptsData.stored_prompt.atnaujinta_data)}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {aiPromptsData.regenerated_prompt && !aiPromptsData.regenerated_prompt.error && (
                    <div style={{
                      padding: '1.5rem',
                      background: 'white',
                      borderRadius: '8px',
                      marginBottom: '1.5rem',
                      border: '1px solid #ffc107'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h4 style={{ margin: 0, color: '#856404' }}> Automatiškai sugeneruotas promptas (peržiūrai)</h4>
                        <span style={{
                          background: '#ffc107',
                          color: '#212529',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.8rem'
                        }}>
                          Tik peržiūrai
                        </span>
                      </div>

                      <div style={{
                        padding: '0.75rem',
                        background: '#fff3cd',
                        borderRadius: '6px',
                        marginBottom: '1rem',
                        fontSize: '0.85rem',
                        color: '#856404'
                      }}>
                        💡 Tai yra automatiškai sugeneruotas promptas iš dabartinių vartotojo duomenų. Naudojamas tik palyginimui.
                      </div>

                      <details>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                           Peržiūrėti automatinį promptą
                        </summary>
                        <div style={{
                          maxHeight: '300px',
                          overflowY: 'auto',
                          background: '#f8f9fa',
                          padding: '1rem',
                          borderRadius: '6px',
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          lineHeight: '1.4',
                          whiteSpace: 'pre-wrap',
                          border: '1px solid #e0e0e0',
                          marginTop: '1rem'
                        }}>
                          {typeof aiPromptsData.regenerated_prompt.generated_prompt === 'object'
                            ? aiPromptsData.regenerated_prompt.generated_prompt.prompt || JSON.stringify(aiPromptsData.regenerated_prompt.generated_prompt, null, 2)
                            : aiPromptsData.regenerated_prompt.generated_prompt}
                        </div>
                      </details>
                    </div>
                  )}

                  {aiPromptsData.regenerated_prompt?.error && (
                    <div style={{
                      padding: '1rem',
                      background: '#f8d7da',
                      borderRadius: '6px',
                      marginBottom: '1rem',
                      color: '#721c24'
                    }}>
                       <strong>Klaida generuojant automatinį promptą:</strong> {aiPromptsData.regenerated_prompt.details}
                    </div>
                  )}

                  {aiPromptsData.summary_prompt && !aiPromptsData.summary_prompt.error && (
                    <div style={{
                      padding: '1.5rem',
                      background: 'white',
                      borderRadius: '8px',
                      marginBottom: '1.5rem',
                      border: '2px solid #17a2b8'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h4 style={{ margin: 0, color: '#17a2b8' }}>📊 AI Santraukos Promptas</h4>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <span style={{
                            background: '#17a2b8',
                            color: 'white',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.8rem'
                          }}>
                            Dinamiškas
                          </span>
                          {!isEditingSummaryPrompt && (
                            <button
                              onClick={startSummaryPromptEdit}
                              style={{
                                background: '#17a2b8',
                                color: 'white',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                            >
                              ✏️ Redaguoti sistemos promptą
                            </button>
                          )}
                        </div>
                      </div>

                      <div style={{
                        padding: '0.75rem',
                        background: aiPromptsData.summary_prompt.is_custom ? '#d4edda' : '#d1ecf1',
                        borderRadius: '6px',
                        marginBottom: '1rem',
                        fontSize: '0.85rem',
                        color: aiPromptsData.summary_prompt.is_custom ? '#155724' : '#0c5460'
                      }}>
                        {aiPromptsData.summary_prompt.is_custom ? (
                          <>✨ Naudojamas <strong>pritaikytas</strong> santraukos promptas profesijai: <strong>{aiPromptsData.summary_prompt.profession}</strong></>
                        ) : (
                          <>💡 Šis promptas naudojamas automatinei skambučių santraukų generavimui pagal profesiją: <strong>{aiPromptsData.summary_prompt.profession}</strong></>
                        )}
                      </div>

                      <details open>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem', color: '#17a2b8' }}>
                          🤖 Sistemos Promptas (System Prompt)
                        </summary>
                        {isEditingSummaryPrompt ? (
                          <>
                            <textarea
                              value={editedSummarySystemPrompt}
                              onChange={(e) => setEditedSummarySystemPrompt(e.target.value)}
                              style={{
                                width: '100%',
                                minHeight: '150px',
                                padding: '1rem',
                                borderRadius: '6px',
                                border: '2px solid #17a2b8',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                lineHeight: '1.4',
                                resize: 'vertical',
                                marginTop: '0.5rem',
                                marginBottom: '1rem'
                              }}
                            />
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                              <button
                                onClick={saveSummaryPromptEdit}
                                disabled={saving}
                                style={{
                                  background: saving ? '#6c757d' : '#17a2b8',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem 1rem',
                                  borderRadius: '4px',
                                  cursor: saving ? 'not-allowed' : 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: '500'
                                }}
                              >
                                {saving ? '⏳ Išsaugoma...' : '✓ Išsaugoti'}
                              </button>
                              <button
                                onClick={cancelSummaryPromptEdit}
                                disabled={saving}
                                style={{
                                  background: '#6c757d',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.5rem 1rem',
                                  borderRadius: '4px',
                                  cursor: saving ? 'not-allowed' : 'pointer',
                                  fontSize: '0.85rem'
                                }}
                              >
                                ✗ Atšaukti
                              </button>
                            </div>
                          </>
                        ) : (
                          <div style={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            background: '#f8f9fa',
                            padding: '1rem',
                            borderRadius: '6px',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            lineHeight: '1.4',
                            whiteSpace: 'pre-wrap',
                            border: '1px solid #e0e0e0',
                            marginTop: '0.5rem',
                            marginBottom: '1rem'
                          }}>
                            {aiPromptsData.summary_prompt.system_prompt}
                          </div>
                        )}
                      </details>

                      <details>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem', color: '#17a2b8' }}>
                          📝 Vartotojo Promptas (User Prompt) - Su Profesijos Kontekstu
                        </summary>
                        <div style={{
                          maxHeight: '400px',
                          overflowY: 'auto',
                          background: '#f8f9fa',
                          padding: '1rem',
                          borderRadius: '6px',
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          lineHeight: '1.4',
                          whiteSpace: 'pre-wrap',
                          border: '1px solid #e0e0e0',
                          marginTop: '0.5rem'
                        }}>
                          {aiPromptsData.summary_prompt.user_prompt}
                        </div>
                      </details>

                      <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#666' }}>
                        ℹ️ Santraukos promptas generuojamas dinamiškai kiekvienam skambučiui pagal vartotojo profesiją ir duomenis.
                      </div>
                    </div>
                  )}

                  {aiPromptsData.summary_prompt?.error && (
                    <div style={{
                      padding: '1rem',
                      background: '#f8d7da',
                      borderRadius: '6px',
                      marginBottom: '1rem',
                      color: '#721c24'
                    }}>
                       <strong>Klaida generuojant santraukos promptą:</strong> {aiPromptsData.summary_prompt.details}
                    </div>
                  )}

                  {!aiPromptsData.stored_prompt && !aiPromptsData.regenerated_prompt && (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#666',
                      background: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}></div>
                      <h4>AI promptas dar nesukurtas</h4>
                      <p>Šiam vartotojui dar nėra sukurtas AI promptas. Jis bus automatiškai sukurtas, kai vartotojas prisijungs prie sistemos.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#dc3545' }}>
                   Nepavyko gauti AI prompto duomenų
                </div>
              )}
            </div>
          )}

          {activeTab === 'usage' && (
            <div className="profile-section">
              <h3> Naudojimo statistika</h3>
              {usageData ? (
                <div>
                  <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                    <div className="stat-card">
                      <h4> Iš viso skambučių</h4>
                      <div className="stat-value">{usageData.summary.totalCalls}</div>
                    </div>
                    <div className="stat-card">
                      <h4> Iš viso minučių</h4>
                      <div className="stat-value">{usageData.summary.totalMinutes}</div>
                    </div>
                    <div className="stat-card">
                      <h4> Vidutinis trukmė</h4>
                      <div className="stat-value">{usageData.summary.avgCallDuration} min</div>
                    </div>
                    <div className="stat-card">
                      <h4>😊 Pasitenkinimo lygis</h4>
                      <div className="stat-value">{usageData.summary.satisfactionRate}%</div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                    <div>
                      <h4> Skambučių tipai</h4>
                      <div className="call-types">
                        {Object.entries(usageData.callTypes).map(([type, count]) => (
                          <div key={type} className="call-type-item">
                            <span>{type}:</span>
                            <strong>{count as number}</strong>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4>🕒 Paskutiniai skambučiai</h4>
                      <div className="recent-calls">
                        {usageData.recentCalls && usageData.recentCalls.length > 0 ? (
                          usageData.recentCalls.map((call: any, index: number) => (
                            <div key={index} className="call-item">
                              <span className="call-time">
                                {new Date(call.sukurta_data).toLocaleDateString('lt-LT')}
                              </span>
                              <span className="call-type">{call.užklausos_tipas}</span>
                              <span className="call-duration">{Math.round((call.trukmė_sekundėmis || 0) / 60)} min</span>
                            </div>
                          ))
                        ) : (
                          <p style={{ color: '#666', fontSize: '0.9rem' }}>Nėra registruotų skambučių</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p>Kraunama naudojimo statistika...</p>
              )}
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="profile-section">
              <h3> Mokėjimų istorija</h3>
              {paymentsData ? (
                <div>
                  <div className="payment-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                    <div className="stat-card">
                      <h4> Iš viso sumokėta</h4>
                      <div className="stat-value">€{paymentsData.totalPaid || 0}</div>
                    </div>
                    <div className="stat-card">
                      <h4> Dabartinis planas</h4>
                      <div className="stat-value">{paymentsData.currentPlan || 'Nemokamas'}</div>
                    </div>
                    <div className="stat-card">
                      <h4> Prenumeratos statusas</h4>
                      <div className="stat-value">
                        {paymentsData.subscriptionStatus || 'Neaktyvus'}
                      </div>
                    </div>
                    {paymentsData.nextPaymentDate && (
                      <div className="stat-card">
                        <h4> Galioja iki</h4>
                        <div className="stat-value">
                          {new Date(paymentsData.nextPaymentDate).toLocaleDateString('lt-LT')}
                        </div>
                      </div>
                    )}
                  </div>

                  <h4> Mokėjimų istorija</h4>
                  {paymentsData.payments && paymentsData.payments.length > 0 ? (
                    <div className="payments-table">
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8f9fa' }}>
                            <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #e0e0e0' }}>Data</th>
                            <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #e0e0e0' }}>Aprašymas</th>
                            <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #e0e0e0' }}>Suma</th>
                            <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #e0e0e0' }}>Būsena</th>
                            <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #e0e0e0' }}>Metodas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentsData.payments.map((payment: any) => (
                            <tr key={payment.id}>
                              <td style={{ padding: '1rem', border: '1px solid #e0e0e0' }}>
                                {new Date(payment.date).toLocaleDateString('lt-LT')}
                              </td>
                              <td style={{ padding: '1rem', border: '1px solid #e0e0e0' }}>
                                {payment.description}
                              </td>
                              <td style={{ padding: '1rem', border: '1px solid #e0e0e0' }}>
                                €{payment.amount.toFixed(2)}
                              </td>
                              <td style={{ padding: '1rem', border: '1px solid #e0e0e0' }}>
                                <span className={`status-badge ${payment.status}`}>
                                  {payment.status === 'paid' ? 'Apmokėta' : payment.status}
                                </span>
                              </td>
                              <td style={{ padding: '1rem', border: '1px solid #e0e0e0' }}>
                                {payment.method === 'card' ? 'Kortelė' : payment.method}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#666',
                      background: '#f8f9fa',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}></div>
                      <p>Mokėjimų istorija tuščia</p>
                      <p style={{ fontSize: '0.9rem' }}>Šis vartotojas dar neturi apmokėjimo įrašų.</p>
                    </div>
                  )}
                </div>
              ) : (
                <p>Kraunama mokėjimų istorija...</p>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="profile-section" style={{
              background: '#f8f9fa',
              padding: '2rem',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <h3 style={{ color: '#000042', marginBottom: '1.5rem', fontSize: '1.5rem' }}>⏱ Veiklos istorija</h3>
              {activityLoading ? (
                <p>Kraunama veiklos istorija...</p>
              ) : activityData.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {activityData.map((activity, index) => (
                    <div key={index} style={{
                      background: 'white',
                      padding: '1rem',
                      borderRadius: '6px',
                      border: '1px solid #e0e0e0',
                      display: 'grid',
                      gridTemplateColumns: '200px 150px 1fr',
                      gap: '1rem',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        fontWeight: '500',
                        color: '#666',
                        fontSize: '0.9rem'
                      }}>
                        {new Date(activity.created_at).toLocaleString('lt-LT', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      <span style={{
                        fontWeight: '600',
                        color: '#000042',
                        fontSize: '0.9rem'
                      }}>
                        {activity.activity_type}
                      </span>
                      <span style={{
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        {activity.activity_description}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#666', fontStyle: 'italic' }}>Veiklos istorija tuščia</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserProfile;